import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import {
  useOpportunities,
  useOpportunityEligibility,
  useSimilarOpportunities,
  useDrivesForOpportunity,
  useApplyToDrive,
  useMyEligibilitySummary,
  type OpportunityRow,
  type OpportunitiesListOpts,
} from '@/lib/queries';
import { formatLPA, formatIndianDate } from '@kollez/shared';

/**
 * /explore — Student's opportunity browser (Page 5/N in the page-by-page pass).
 *
 * Deep-audit fixes vs the old page:
 *   - Hardcoded 'Hyderabad' and '₹7 LPA+' filter chips → real city dropdown +
 *     CTC min slider mapped to Pass 4 params.
 *   - Fabricated eligibility drawer (hand-rolled from schema constants) → real
 *     GET /opportunities/:id/eligibility with reason codes.
 *   - Toast-only Apply → real POST /applications via /drives lookup.
 *   - Fake `location`/`tier`/`applied` fields cast onto rows → dropped.
 *   - Empty-page fetches (no pagination) → server pagination + Pass 4 filters.
 *   - No 'similar opportunities' surface → Pass 8 /similar in the drawer footer.
 *
 * No new server API needed. Every knob the page exposes maps to Pass 4 or
 * Pass 8 endpoints that already exist.
 */

type SortKey = NonNullable<OpportunitiesListOpts['sortBy']>;
const PAGE_SIZE = 25;

function initials(name?: string): string {
  return (name ?? '?').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 3).toUpperCase();
}

function primaryCity(row: OpportunityRow): string {
  return row.locations?.[0]?.city ?? '—';
}

/** Debounce a value by N ms so search doesn't fire on every keystroke. */
function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function ExplorePage() {
  const [q, setQ] = useState('');
  const [type, setType] = useState<string>('');
  const [workMode, setWorkMode] = useState<string>('');
  const [ctcMin, setCtcMin] = useState<number | ''>('');
  const [sortBy, setSortBy] = useState<SortKey>('createdAt');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const toast = useToast();
  const debQ = useDebounced(q, 250);

  const listQuery = useOpportunities({
    page,
    limit: PAGE_SIZE,
    q: debQ.trim() || undefined,
    status: 'Active',
    type: type || undefined,
    workMode: workMode || undefined,
    ctcMin: typeof ctcMin === 'number' ? ctcMin : undefined,
    sortBy,
    sortDir: sortBy === 'compensation.ctc.max' ? 'desc' : 'desc',
  });
  const rows = listQuery.data?.opportunities ?? [];
  const summary = listQuery.data?.summary?.byStatus ?? {};
  const totalActive = summary.Active ?? rows.length;

  const openRow = openId ? rows.find((r) => r._id === openId) : null;

  return (
    <div className="stack">
      <style>{`
        .op { display: grid; gap: 10px; padding: 16px; cursor: pointer; text-align: left; }
        .op:hover { border-color: rgb(var(--line-primary)); }
        .op-top { display: flex; gap: 12px; align-items: flex-start; }
        .req { display: grid; gap: 8px; margin: 0; padding: 0; list-style: none; }
        .req li { display: grid; grid-template-columns: 20px 1fr auto; gap: 8px; font-size: .875rem; align-items: center; }
        .req .ok { color: rgb(var(--ink-success)); }
        .req .no { color: rgb(var(--ink-danger)); }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Explore opportunities</h1>
          <p className="subhead">
            {totalActive === 0
              ? 'No live opportunities right now. Check back soon — new roles land every week.'
              : `${totalActive} live opportunit${totalActive === 1 ? 'y' : 'ies'}. Tap one to check if you can apply.`}
          </p>
        </div>
      </div>

      <EligibilitySummaryCard />

      <div className="grid gap-3">
        <div className="input-group">
          <Icon name="search" size={14} />
          <input
            className="input"
            placeholder="Company, role or city"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <select className="select" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>
            <option value="">All types</option>
            <option value="Placement_OnCampus">Placement (on-campus)</option>
            <option value="Placement_OffCampus">Placement (off-campus)</option>
            <option value="Internship">Internship</option>
            <option value="Training">Training</option>
            <option value="Workshop">Workshop</option>
            <option value="Hackathon">Hackathon</option>
            <option value="Certification">Certification</option>
            <option value="Mentorship">Mentorship</option>
            <option value="Contract_Position">Contract</option>
            <option value="R&D_Project">R&amp;D project</option>
          </select>
          <select className="select" value={workMode} onChange={(e) => { setWorkMode(e.target.value); setPage(1); }}>
            <option value="">Any work mode</option>
            <option value="On_Site">On-site</option>
            <option value="Remote">Remote</option>
            <option value="Hybrid">Hybrid</option>
          </select>
          <select className="select" value={String(ctcMin)} onChange={(e) => { const v = e.target.value; setCtcMin(v === '' ? '' : Number(v)); setPage(1); }}>
            <option value="">Any CTC</option>
            <option value="300000">₹3 LPA+</option>
            <option value="500000">₹5 LPA+</option>
            <option value="700000">₹7 LPA+</option>
            <option value="1000000">₹10 LPA+</option>
            <option value="1500000">₹15 LPA+</option>
          </select>
          <select className="select" value={sortBy} onChange={(e) => { setSortBy(e.target.value as SortKey); setPage(1); }}>
            <option value="createdAt">Most recent</option>
            <option value="compensation.ctc.max">Highest CTC</option>
            <option value="applicationDeadline">Deadline soonest</option>
            <option value="title">Title A→Z</option>
          </select>
        </div>
      </div>

      <AsyncBoundary
        kind="cards"
        loading={listQuery.isLoading}
        error={listQuery.isError ? (listQuery.error instanceof Error ? listQuery.error.message : true) : undefined}
        empty={!listQuery.isLoading && rows.length === 0}
        emptyTitle="No opportunities match those filters"
        emptyHint="Try widening the CTC or type filter, or clear the search."
        onRetry={() => void listQuery.refetch()}
      >
        <div className="grid gap-3">
          {rows.map((r) => {
            const partnerName = r.partner?.name ?? 'Company';
            const city = primaryCity(r);
            const ctc = r.compensation?.ctc?.min ?? r.compensation?.ctc?.max ?? 0;
            const closesAt = (r as unknown as { applicationSettings?: { closesAt?: string } }).applicationSettings?.closesAt;
            const closed = closesAt ? new Date(closesAt) < new Date() : false;
            return (
              <button
                key={r._id}
                className="card op"
                onClick={() => setOpenId(r._id)}
              >
                <div className="op-top">
                  <span className="logo-tile">{initials(partnerName)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="cell-title">{partnerName}</div>
                    <div className="text-sm text-ink-secondary">{r.title}</div>
                  </div>
                  {closed
                    ? <span className="badge">Closed</span>
                    : <span className="badge badge-success">Open</span>}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-secondary">
                  {ctc ? <span className="font-semibold text-ink-primary">{formatLPA(ctc)}</span> : null}
                  <span>{city}</span>
                  <span>{closesAt ? `Closes ${formatIndianDate(closesAt)}` : 'No deadline'}</span>
                  <span className="helper">· {r.type.replace(/_/g, ' ')}</span>
                  {typeof r.applicationCount === 'number' && r.applicationCount > 0 ? (
                    <span className="helper">· {r.applicationCount} applied</span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </AsyncBoundary>

      {(rows.length === PAGE_SIZE || page > 1) && (
        <div className="flex items-center justify-between gap-2 mt-2">
          <span className="helper">Page {page}</span>
          <div className="flex gap-2">
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
            <button className="btn btn-ghost btn-sm" disabled={rows.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      )}

      {/* Eligibility drawer — real API */}
      <Drawer
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        title={openRow?.partner?.name ?? 'Opportunity'}
        size="md"
      >
        {openRow ? (
          <EligibilityBody
            row={openRow}
            onApplied={() => {
              toast.show({ message: `Applied to ${openRow.partner?.name ?? 'this drive'}` });
              setOpenId(null);
            }}
          />
        ) : null}
      </Drawer>
    </div>
  );
}

function EligibilityBody({ row, onApplied }: { row: OpportunityRow; onApplied: () => void }) {
  const eligibility = useOpportunityEligibility(row._id);
  const similar = useSimilarOpportunities(row._id, 4);
  const drives = useDrivesForOpportunity(row._id);
  const apply = useApplyToDrive();

  const partnerName = row.partner?.name ?? 'Company';
  const city = primaryCity(row);
  const ctc = row.compensation?.ctc?.min ?? row.compensation?.ctc?.max ?? 0;
  const closesAt = (row as unknown as { applicationSettings?: { closesAt?: string } }).applicationSettings?.closesAt;

  const canApply = eligibility.data?.eligible === true;
  const reasons = eligibility.data?.reasons ?? [];
  const activeDrive = drives.data?.drives?.[0];

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <span className="logo-tile">{initials(partnerName)}</span>
        <div>
          <h2 className="h-section">{partnerName}</h2>
          <p className="helper">{row.title}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="well p-3"><div className="helper">CTC</div><b className="text-lg">{ctc ? formatLPA(ctc) : '—'}</b></div>
        <div className="well p-3"><div className="helper">Location</div><b>{city}</b></div>
      </div>

      <h3 className="text-sm font-semibold mb-2">Can you apply?</h3>
      {eligibility.isLoading ? (
        <p className="helper">Checking your eligibility…</p>
      ) : eligibility.isError ? (
        <p className="helper" role="alert">Couldn't check eligibility. Try refreshing.</p>
      ) : canApply ? (
        <div className="notice notice-success mb-4">
          <Icon name="check" size={14} />
          <div>You're eligible for this opportunity.</div>
        </div>
      ) : (
        <ul className="req mb-4">
          {reasons.map((r) => (
            <li key={r.code}>
              <span className="no"><Icon name="alert-triangle" size={14} /></span>
              <span>{r.message}</span>
              <span className="helper">
                {typeof r.required !== 'undefined' && typeof r.actual !== 'undefined'
                  ? `required ${String(r.required)}, yours ${String(r.actual)}`
                  : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      <dl className="dl mb-4">
        <dt>Closes</dt><dd>{closesAt ? formatIndianDate(closesAt) : 'No deadline'}</dd>
        <dt>Type</dt><dd>{row.type.replace(/_/g, ' ')}</dd>
        <dt>Scope</dt><dd>{row.scope}</dd>
      </dl>

      {similar.data?.items && similar.data.items.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold mb-2">Similar opportunities</h3>
          <ul className="grid gap-2 list-none p-0 m-0">
            {similar.data.items.map((s) => (
              <li key={s._id}>
                <Link to={`/opportunities/${s._id}`} className="link text-sm">
                  {s.partner?.name ?? 'Company'} — {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Link className="link text-sm inline-flex items-center gap-1" to="/nexie">
        <Icon name="sparkles" size={14} /> Ask Nexie how to prepare for {partnerName}
      </Link>

      <div className="drawer-foot flex-wrap mt-4">
        <Link className="btn btn-ghost" to={`/opportunities/${row._id}`}>Full details</Link>
        <button
          className="btn btn-primary ml-auto"
          disabled={!canApply || !activeDrive || apply.isPending}
          onClick={() => {
            if (!activeDrive) return;
            void apply.mutateAsync({ drive: activeDrive._id })
              .then(onApplied)
              .catch((e: unknown) => {
                const msg = e instanceof Error ? e.message : 'Apply failed';
                // eslint-disable-next-line no-alert
                alert(msg);
              });
          }}
        >
          {!activeDrive ? 'No active drive' : apply.isPending ? 'Applying…' : 'Apply'}
        </button>
      </div>
    </>
  );
}

/**
 * Ship 5d/5g "why can't I apply?" panel — reads /students/me/eligibility-summary
 * (Pass 11 relationship endpoint). Deterministic evaluation over every active
 * drive in the caller's tenant scope: shows eligible count + top blockers with
 * the rule code + human-readable sample message. Self-hides when the tenant has
 * zero live drives.
 */
function EligibilitySummaryCard() {
  const q = useMyEligibilitySummary();
  const data = q.data;
  if (q.isLoading || !data || data.totalActive === 0) return null;
  const pct = data.totalActive > 0 ? Math.round((data.eligible / data.totalActive) * 100) : 0;
  return (
    <section className="card card-pad grid gap-3" data-testid="eligibility-summary">
      <div>
        <h2 className="h-section">Where you stand</h2>
        <p className="helper m-0">
          You're eligible for <b>{data.eligible}</b> of {data.totalActive} live drives.
          {data.blockers.length > 0 && ' Top reasons drives are gated:'}
        </p>
      </div>
      <div className="bar"><span style={{ width: `${pct}%` }} /></div>
      {data.eligible > 0 && (
        <div>
          <Link to="/open-to-me" className="link">See the {data.eligible} drive{data.eligible === 1 ? '' : 's'} open to you →</Link>
        </div>
      )}
      {data.blockers.length > 0 && (
        <ol className="stack">
          {data.blockers.slice(0, 4).map((b) => (
            <li key={b.code} className="flex items-start gap-2">
              <span className="badge badge-warning">
                {b.count} drive{b.count === 1 ? '' : 's'}
              </span>
              <div className="min-w-0">
                <div className="font-medium">{b.sampleMessage}</div>
                <div className="cell-sub mono">{b.code}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
